import { useMemo, useRef, useEffect } from "react";
import { api, useAppDispatch, useAppSelector } from "@litres-core/store";

import { useHasWindow } from "@litres-libs/global-hooks";
import { getCookie } from "@litres/utils";
import { setSafeModeCookie, deleteSafeModeCookie } from "./utils/helpers";
import { ESafemode, ESafeModeStatus as EStatus } from "./constants";

export type SafeModeStatusType = keyof typeof EStatus;

export const useSafemode = () => {
  const hasWindow = useHasWindow();
  const safeModeStatusFromCookie = (hasWindow && getCookie("safemode")) || "";
  const isClosedSafeMode = (hasWindow && getCookie("isClosedSafeMode")) || "";
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.foundationUser);
  const isAuthenticated = useMemo<boolean>(
    () => !!(user.id && user.login),
    [user]
  );
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated);
  const isLogOff =
    isAuthenticatedRef.current === true && isAuthenticated === false;

  const isSafeModeEnabledUser = useAppSelector(
    state => state.foundationUser.settings.is_safemode_enabled
  );
  const isChanging = useRef(false);
  const isDeleting = useRef(false);

  // Статус safeMode, который формируется в зависимости от условий
  const safeModeStatus = useMemo<SafeModeStatusType>(() => {
    const safeModeUser =
      isSafeModeEnabledUser === null
        ? EStatus.undefined
        : isSafeModeEnabledUser
          ? EStatus.enabled
          : EStatus.disabled;
    // Если пользователь залогинен и он имеет safemode, то данные берем из стора
    if (isAuthenticated && safeModeUser !== null) {
      return safeModeUser;
    }
    // Если есть значения в куках, то берем из кук
    if (safeModeStatusFromCookie) {
      return safeModeStatusFromCookie === "1"
        ? EStatus.enabled
        : EStatus.disabled;
    }

    // Если в куках ничего нету, то берем данные из стора
    return safeModeUser;
  }, [isSafeModeEnabledUser, safeModeStatusFromCookie]);

  const safeModeStatusRef = useRef(safeModeStatus);
  const isSafeModeEnabled =
    safeModeStatus === EStatus.enabled &&
    safeModeStatus !== safeModeStatusRef.current;

  const changeSafeMode = (isEnabled: boolean) => {
    // Защита, чтобы не вызывать логику ниже на сервере.
    // Сейчас предполагается вызов только на клиенте.
    if (!hasWindow) {
      return;
    }
    // Не вызываем ещё раз если происходит изменение данных
    if (isChanging.current || isDeleting.current) {
      return;
    }
    isChanging.current = true;
    const status = isEnabled ? ESafemode.hide : ESafemode.show;
    if (getCookie("safemode") !== status.toString()) {
      setSafeModeCookie(status);
      dispatch(api.browser.setHeader(["Safemode-Enabled", `${status}`]));
    }
    if (!!isSafeModeEnabledUser !== isEnabled) {
      dispatch(
        api.foundationUser.setFoundationUserAccountSettings({
          is_safemode_enabled: isEnabled,
        })
      );
    }
    isChanging.current = false;
  };
  const deleteSafeMode = () => {
    isDeleting.current = true;
    deleteSafeModeCookie();
    dispatch(api.browser.removeHeader("Safemode-Enabled"));
    dispatch(
      api.foundationUser.setFoundationUserAccountSettings({
        is_safemode_enabled: null,
      })
    );
    isDeleting.current = false;
  };

  // Если происходит logOff юзера, то мы удаляем safemode
  useEffect(() => {
    if (isLogOff) {
      deleteSafeMode();
    }
  }, [isLogOff, isAuthenticated]);

  // синхронизируем настройки
  useEffect(() => {
    if (safeModeStatus !== EStatus.undefined && !isSafeModeEnabled) {
      changeSafeMode(safeModeStatus === EStatus.enabled);
    }
  }, [hasWindow, safeModeStatus, isAuthenticated, safeModeStatusFromCookie]);

  return {
    isClosedSafeMode,
    safeModeStatus,
    isSafeModeEnabled,
    changeSafeMode,
  };
};
